import { SelectOption } from '@globaleris/ui.library'
import moment from 'moment'
import {
  AddTradeRequest,
  Broker,
  CheckCounterpartTradeRulebookRequest,
  CheckEndDateTractRulebookRequest,
  CheckEndDateTradeRulebookRequest,
  CheckLogisticElementRulebookRequest,
  CheckQuantityRulebookRequest,
  CheckStartDateTractRulebookRequest,
  CheckStartDateTradeRulebookRequest,
  CheckTradeSimpleResponse,
  CicloFacturacion,
  Contrato,
  FetchAPIRequest,
  FetchRequest,
  PatchTradePioneerRequest,
  PosicionPrecio,
  RulebookResponse,
  TradeDetailsRequest,
  TradeDetailsResponse,
  ValidatePositionRulebookModel,
  ValidatePositionRulebookRequest,
  ValidateTractRulebookRequest,
  ValidateTradeRulebookModel,
  ValidateTradeRulebookRequest,
  GeneratePositionCodeAPIRequest,
  GeneratePositionCodeAPIResponse,
  NewPosicionPrecio,
  CheckPositionTypeRulebookRequest,
  CheckDateTimeLessThanRulebookRequest,
  CheckPastTradeRulebookRequest,
  CheckDeletePositionRulebookRequest,
  ValidateTractRulebookModel,
  NewTramo,
  PosicionPrecioDiff,
  PriceConceptRequest,
  CreatePriceForPositionResponse,
  CreatePriceForPositionRequest,
  CheckEndDatePositionRulebookRequest,
  CheckDeleteTractRulebookRequest,
  CheckPresentFutureDatesRulebookRequest,
  CheckPresentFutureDatesTractRequest,
  Tract,
  EnumTipoPosicion
} from '../../api'
import { ApiContracts } from '../../api/Pioneer/endpoints/contracts/contracts.api'
import { ApiLogisticElements } from '../../api/Pioneer/endpoints/logisticElements/logisticElements.api'
import { fetchBrokerAPI, fetchSubjectsAPI } from '../../api/Pioneer/endpoints/subjects/subjects.api'
import { ApiTrades } from '../../api/Pioneer/endpoints/trades/trades.api'
import { ContractTypes } from '../../api/Pioneer/enums/contractTypes'
import { MeasureUnitType } from '../../api/Pioneer/enums/measureUnitType'
import { SubjectTypes } from '../../api/Pioneer/enums/subjectTypes'
import { Currency } from '../../api/Pioneer/model/Currency'
import { MeasureUnit } from '../../api/Pioneer/model/MeasureUnit'
import { UnitPrice } from '../../api/Pioneer/model/UnitPrice'
import { enumEnumToValueNumber, enumKeyToValue } from '../../helpers'
import {
  createErrorServiceResponse,
  createSuccessServiceResponse
} from '../../helpers/serviceResponseHelper'
import { DateFilters } from '../../model'
import { ServiceResponse } from '../../model/serviceResponse'
import { getActiveBalanceZone, getActiveCompany } from '../../redux'
import { DatesInterval } from './components/positionForm/positionForm'
import {
  formatTradeResponse,
  mapAddTradeRequest,
  mapPositionAddTradeRequest,
  mapCreatePositionRequest,
  mapDeletePositionRequest,
  mapEditPositionRequest,
  mapToOdataRequestSimple,
  mapTradeListItemsViewModel,
  mapTradesFetchRequest,
  mapAddComplexTradeRequest
} from './trades.mapper'
import { FetchTradesRequest } from './trades.model'
import { apiFetchInvoiceCycles } from '../../api/Pioneer/endpoints/invoiceCycles/invoiceCycles.api'

const apiTrades = new ApiTrades()
const apiLogisticElements = new ApiLogisticElements()
const apiContracts = new ApiContracts()

export const fetchTrades = async (request: FetchTradesRequest): Promise<ServiceResponse<any>> => {
  switch (request.dateFilter) {
    case DateFilters.ACTIVES: {
      return await fetchActives(mapTradesFetchRequest(request))
    }
    case DateFilters.ACTIVES_AND_FUTURES: {
      return await fetchActivesAndFutures(mapTradesFetchRequest(request))
    }
    default: {
      return await fetchCustom(mapTradesFetchRequest(request))
    }
  }
}

export const fetchActives = async (request: FetchRequest): Promise<ServiceResponse<any>> => {
  const formattedRequest: FetchAPIRequest = {
    ...request,
    initDate: request.initDate ? moment(request.initDate).format('YYYY-MM-DD') : undefined,
    endDate: request.endDate ? moment(request.endDate).format('YYYY-MM-DD') : undefined
  }
  return await apiTrades
    .FetchActives(formattedRequest)
    .then(response => {
      return createSuccessServiceResponse(mapTradeListItemsViewModel(response))
    })
    .catch(error => {
      return createErrorServiceResponse(error)
    })
}

export const fetchActivesAndFutures = async (
  request: FetchRequest
): Promise<ServiceResponse<any>> => {
  const formattedRequest: FetchAPIRequest = {
    ...request,
    initDate: request.initDate ? moment(request.initDate).format('YYYY-MM-DD') : undefined,
    endDate: request.endDate ? moment(request.endDate).format('YYYY-MM-DD') : undefined
  }
  return await apiTrades
    .FetchActivesAndFutures(formattedRequest)
    .then(response => {
      return createSuccessServiceResponse(mapTradeListItemsViewModel(response))
    })
    .catch(error => {
      return createErrorServiceResponse(error)
    })
}

export const fetchCustom = async (request: FetchRequest): Promise<ServiceResponse<any>> => {
  const formattedRequest: FetchAPIRequest = {
    ...request,
    initDate: request.initDate ? moment(request.initDate).format('YYYY-MM-DD') : undefined,
    endDate: request.endDate ? moment(request.endDate).format('YYYY-MM-DD') : undefined
  }
  return await apiTrades
    .FetchCustom(formattedRequest)
    .then(response => {
      return createSuccessServiceResponse(mapTradeListItemsViewModel(response))
    })
    .catch(error => {
      return createErrorServiceResponse(error)
    })
}

export const countTrades = async (request: FetchTradesRequest): Promise<number | undefined> => {
  return await apiTrades
    .FetchCustomCount(mapTradesFetchRequest(request))
    .then(response => {
      return response
    })
    .catch(() => {
      return undefined
    })
}

//#region Search form dependencies

export const fetchCounterParts = async (): Promise<SelectOption[]> => {
  const data = await fetchSubjectsAPI([SubjectTypes.Wholesaler], getActiveBalanceZone()?.id!)
  if (data && data.length > 0) {
    return data.sort((a, b) => a.label.localeCompare(b.label))
  } else {
    return []
  }
}

export const fetchCounterPartsExtendedClients = async (): Promise<SelectOption[]> => {
  const data = await fetchSubjectsAPI([SubjectTypes.ExtendedClient], null)
  if (data && data.length > 0) {
    return data.sort((a, b) => a.label.localeCompare(b.label))
  } else {
    return []
  }
}

export const fetchDeliveryPoints = async (zone): Promise<SelectOption[]> => {
  const data = await apiLogisticElements.Fetch(zone)
  if (data && data.length > 0) {
    return data.sort((a, b) => a.label.localeCompare(b.label))
  } else {
    return []
  }
}

export const fetchDeliveryPointsForExtendedClient = async (zone): Promise<SelectOption[]> => {
  const data = await apiLogisticElements.FetchBySubtype(zone, [35])
  if (data && data.length > 0) {
    const fictionalElement = data.find(data => data?.label === 'Punto Virtual Ficticio')
    return fictionalElement ? [fictionalElement] : []
  } else {
    return []
  }
}

export const fetchContractsOptions = async (subject: number): Promise<SelectOption[]> => {
  return await apiContracts.FetchOptions(subject, getActiveBalanceZone()?.id!, ContractTypes.Trade)
}

export const fetchContracts = async (subject: number): Promise<Contrato[]> => {
  return await apiContracts.FetchContractsToTrades(
    subject,
    ContractTypes.Trade,
    getActiveBalanceZone()?.id!
  )
}

export const fetchContractsForExtendedClient = async (subject: number): Promise<Contrato[]> => {
  return await apiContracts.FetchContractsToTrades(
    subject,
    ContractTypes.ClienteExtendido,
    getActiveBalanceZone()?.id!
  )
}

export const fetchTradeTypes = async (): Promise<SelectOption[]> => {
  return await apiTrades.FetchTradeTypes()
}

export const fetchBooks = async (): Promise<SelectOption[]> => {
  return await apiTrades.FetchBooks()
}

export const fetchBrokers = async (): Promise<SelectOption[]> => {
  return await fetchBrokerAPI([SubjectTypes.Commercial], getActiveBalanceZone()?.id!)
}

export const fetchContractedProducts = async (broker: number | string): Promise<SelectOption[]> => {
  return await apiTrades.FetchContractedProducts(broker)
}

export const fetchInvoiceCycles = async (invoiceCycleType: number): Promise<CicloFacturacion[]> => {
  return await apiFetchInvoiceCycles(invoiceCycleType)
}

export const fetchCurrencies = async (): Promise<Currency[]> => {
  return await apiTrades.FetchCurrencies()
}

export const fetchEnergyMeasureUnits = async (): Promise<MeasureUnit[]> => {
  const response = await apiTrades.FetchMeasureUnits()
  return response.filter(
    mu => mu.unitType === enumKeyToValue(MeasureUnitType, MeasureUnitType.Energía)
  )
}

export const fetchUnitPricesByEnergyType = async (): Promise<UnitPrice[]> => {
  return await apiTrades.FetchUnitPricesByType(2)
}

//#endregion

export const createComplexTrade = async (
  trade: AddTradeRequest,
  brokers: Broker[],
  tradeAditionals: Partial<AddTradeRequest> | undefined,
  positionTableData?: any
): Promise<ServiceResponse<any>> => {
  trade.positions = mapPositionAddTradeRequest(positionTableData)
  return await apiTrades
    .CreateTrade(
      mapAddComplexTradeRequest(trade, brokers, tradeAditionals, getActiveCompany()?.id!)
    )
    .then(response => {
      return createSuccessServiceResponse(response)
    })
    .catch(error => {
      return createErrorServiceResponse(error)
    })
}

export const createSimpleTrade = async (
  trade: AddTradeRequest,
  brokers: Broker[],
  tradeAditionals: Partial<AddTradeRequest> | undefined,
  price?: PriceConceptRequest
): Promise<ServiceResponse<any>> => {
  return await apiTrades
    .CreateTrade(
      mapAddTradeRequest(
        trade,
        brokers,
        tradeAditionals,
        getActiveCompany()?.id!,
        price?.expression,
        price?.priceUnit,
        price?.priceCalculationType,
        price?.rounding
      )
    )
    .then(response => {
      return createSuccessServiceResponse(response)
    })
    .catch(error => {
      return createErrorServiceResponse(error)
    })
}

export const generatePositionCode = async (
  request: GeneratePositionCodeAPIRequest
): Promise<ServiceResponse<GeneratePositionCodeAPIResponse | undefined>> => {
  return await apiTrades
    .GeneratePositionCode(request)
    .then(response => {
      return createSuccessServiceResponse(response)
    })
    .catch(error => {
      return createErrorServiceResponse(error)
    })
}

export const deleteTradesItems = async (tradeId: number): Promise<ServiceResponse<any>> => {
  return await apiTrades
    .DeleteTrade(tradeId)
    .then(response => {
      return createSuccessServiceResponse(response)
    })
    .catch(error => {
      return createErrorServiceResponse(error)
    })
}

export const getTradeDetails = async (
  tradeId: number
): Promise<ServiceResponse<TradeDetailsResponse | undefined>> => {
  return await apiTrades
    .GetTradeById(tradeId)
    .then(response => {
      return createSuccessServiceResponse(formatTradeResponse(response))
    })
    .catch(error => {
      return createErrorServiceResponse(error)
    })
}

export const patchTrade = async (
  originalTrade: TradeDetailsRequest,
  request: Partial<PatchTradePioneerRequest>
) => {
  const odataRequest = mapToOdataRequestSimple(request, originalTrade)
  return await apiTrades
    .PatchTrade(originalTrade.id, odataRequest)
    .then(response => {
      return createSuccessServiceResponse(response)
    })
    .catch(error => {
      return createErrorServiceResponse(error)
    })
}

export const checkTradeSimple = async (
  tradeID: number
): Promise<ServiceResponse<CheckTradeSimpleResponse | undefined>> => {
  return await apiTrades
    .CheckTradeSimple(tradeID)
    .then(response => {
      return createSuccessServiceResponse(response)
    })
    .catch(error => {
      return createErrorServiceResponse(error)
    })
}

export const createPriceForPosition = async (
  tradeID: number,
  positionID: number,
  price: PriceConceptRequest
): Promise<ServiceResponse<CreatePriceForPositionResponse | undefined>> => {
  const request: CreatePriceForPositionRequest = {
    tradeID,
    positionID,
    price
  }
  return await apiTrades
    .CreatePriceForPosition(request)
    .then(response => {
      return createSuccessServiceResponse(response)
    })
    .catch(error => {
      return createErrorServiceResponse(error)
    })
}

// Rulebook Validations

export const checkCounterpartRulebook = async (
  tradeId: number,
  counterpartId: number
): Promise<ServiceResponse<any>> => {
  const request: CheckCounterpartTradeRulebookRequest = {
    tradeId: tradeId,
    counterpartId: counterpartId
  }
  return await apiTrades
    .CheckCounterpartTradeRulebook(request)
    .then(response => {
      return createSuccessServiceResponse(response)
    })
    .catch(error => {
      return createErrorServiceResponse(error)
    })
}

export const checkLogElementRulebook = async (
  tradeId: number,
  positionId: number,
  deliveryPointId: number,
  oldDeliveryPointId: number
): Promise<ServiceResponse<RulebookResponse | undefined>> => {
  const request: CheckLogisticElementRulebookRequest = {
    tradeId,
    positionId,
    deliveryPoint: deliveryPointId,
    oldDeliveryPoint: oldDeliveryPointId
  }
  return await apiTrades
    .CheckLogisticElementRulebook(request)
    .then(response => {
      return createSuccessServiceResponse(response)
    })
    .catch(error => {
      return createErrorServiceResponse(error)
    })
}

export const checkQuantityRulebook = async (
  positionId: number,
  counterpartId: number,
  startDate: Date,
  quantity: number,
  endDate?: Date,
  oldQuantity?: number
): Promise<ServiceResponse<RulebookResponse | undefined>> => {
  const request: CheckQuantityRulebookRequest = {
    positionId: positionId,
    counterpart: counterpartId,
    startDate: moment(startDate).format('YYYY-MM-DD'),
    endDate: endDate ? moment(endDate).format('YYYY-MM-DD') : undefined,
    quantity: quantity,
    oldQuantity: oldQuantity
  }
  return await apiTrades
    .CheckQuantityRulebook(request)
    .then(response => {
      return createSuccessServiceResponse(response)
    })
    .catch(error => {
      return createErrorServiceResponse(error)
    })
}

export const checkPositionTypeRulebook = async (
  tradeId: number,
  positionId: number,
  counterpartId: number
): Promise<ServiceResponse<RulebookResponse | undefined>> => {
  const request: CheckPositionTypeRulebookRequest = {
    tradeId: tradeId,
    positionId: positionId,
    counterpart: counterpartId
  }
  return await apiTrades
    .CheckPositionTypeRulebook(request)
    .then(response => {
      return createSuccessServiceResponse(response)
    })
    .catch(error => {
      return createErrorServiceResponse(error)
    })
}

export const checkDateTimeLessThanRulebook = async (
  startDate: Date
): Promise<ServiceResponse<RulebookResponse | undefined>> => {
  const request: CheckDateTimeLessThanRulebookRequest = {
    date: moment(startDate).format('YYYY-MM-DD')
  }
  return await apiTrades
    .CheckDateTimeLessThanRulebook(request)
    .then(response => {
      return createSuccessServiceResponse(response)
    })
    .catch(error => {
      return createErrorServiceResponse(error)
    })
}

export const checkCreatePastTradeRulebook = async (
  counterpartId: number
): Promise<ServiceResponse<RulebookResponse | undefined>> => {
  const request: CheckPastTradeRulebookRequest = { counterpartId: counterpartId }
  return await apiTrades
    .CheckCreatePastTradeRulebook(request)
    .then(response => {
      return createSuccessServiceResponse(response)
    })
    .catch(error => {
      return createErrorServiceResponse(error)
    })
}

export const checkStartDateTradeRulebook = async (
  tradeId: number,
  counterpartId: number,
  startDate: Date,
  previousDate: Date
): Promise<ServiceResponse<RulebookResponse | undefined>> => {
  const request: CheckStartDateTradeRulebookRequest = {
    tradeId: tradeId,
    startDate: startDate,
    oldDate: previousDate,
    counterpartId: counterpartId
  }
  return await apiTrades
    .CheckStartDateTradeRulebook(request)
    .then(response => {
      return createSuccessServiceResponse(response)
    })
    .catch(error => {
      return createErrorServiceResponse(error)
    })
}

export const checkEndDateTradeRulebook = async (
  tradeId: number,
  counterpartId: number,
  endDate?: Date | null,
  previousDate?: Date | null
): Promise<ServiceResponse<RulebookResponse | undefined>> => {
  const request: CheckEndDateTradeRulebookRequest = {
    tradeId: tradeId,
    endDate: endDate ?? null,
    oldDate: previousDate ?? null,
    counterpartId: counterpartId
  }
  return await apiTrades
    .CheckEndDateTradeRulebook(request)
    .then(response => {
      return createSuccessServiceResponse(response)
    })
    .catch(error => {
      return createErrorServiceResponse(error)
    })
}

export const checkEndDatePositionRulebook = async (
  tradeId: number,
  positionId: number,
  positionEndDate: Date,
  tradeStartDate: Date,
  tradeEndDate: Date,
  counterpartId: number
): Promise<ServiceResponse<RulebookResponse | undefined>> => {
  const request: CheckEndDatePositionRulebookRequest = {
    tradeId,
    positionId,
    positionEndDate: moment(positionEndDate).format('YYYY-MM-DD'),
    tradeStartDate: moment(tradeStartDate).format('YYYY-MM-DD'),
    tradeEndDate: moment(tradeEndDate).format('YYYY-MM-DD'),
    counterpartId: counterpartId
  }
  return await apiTrades
    .CheckEndDatePositionRulebook(request)
    .then(response => {
      return createSuccessServiceResponse(response)
    })
    .catch(error => {
      return createErrorServiceResponse(error)
    })
}

export const checkStartDateTractRulebook = async (
  tradeId: number,
  positionId: number,
  startDate: string,
  previousDate: string,
  endDate?: string,
  positionStartDate?: Date,
  positionEndDate?: Date,
  counterpart?: number
): Promise<ServiceResponse<RulebookResponse | undefined>> => {
  const request: CheckStartDateTractRulebookRequest = {
    tradeId: tradeId,
    positionId: positionId,
    tractEndDate: endDate,
    oldTractStartDate: previousDate,
    tractStartDate: startDate,
    positionStartDate: positionStartDate
      ? moment(positionStartDate).format('YYYY-MM-DD')
      : undefined,
    positionEndDate: positionEndDate ? moment(positionEndDate).format('YYYY-MM-DD') : undefined,
    counterpart: counterpart
  }
  return await apiTrades
    .CheckStartDateTractRulebook(request)
    .then(response => {
      return createSuccessServiceResponse(response)
    })
    .catch(error => {
      return createErrorServiceResponse(error)
    })
}

export const checkEndDateTractRulebook = async (
  tradeId: number,
  positionId: number,
  endDate: Date,
  startDate?: Date,
  oldEndDate?: Date | null,
  parentStartDate?: Date,
  parentEndDate?: Date,
  counterpart?: number
): Promise<ServiceResponse<RulebookResponse | undefined>> => {
  const formatNewEndDate = moment(endDate).format('YYYY-MM-DD')
  const formatStartDate = moment(startDate).format('YYYY-MM-DD')
  const formatOldEndDate = oldEndDate ? moment(oldEndDate).format('YYYY-MM-DD') : null
  const request: CheckEndDateTractRulebookRequest = {
    tradeId: tradeId,
    positionId: positionId,
    tractStartDate: formatStartDate,
    tractEndDate: formatNewEndDate,
    oldTractEndDate: formatOldEndDate,
    positionStartDate: parentStartDate ? moment(parentStartDate).format('YYYY-MM-DD') : undefined,
    positionEndDate: parentEndDate ? moment(parentEndDate).format('YYYY-MM-DD') : undefined,
    counterpart: counterpart
  }
  return await apiTrades
    .CheckEndDateTractRulebook(request)
    .then(response => {
      return createSuccessServiceResponse(response)
    })
    .catch(error => {
      return createErrorServiceResponse(error)
    })
}

export const checkPresentFutureDatesRulebook = async (
  tradeId: number,
  positionId: number,
  counterpart: number,
  tracts: NewTramo[]
): Promise<ServiceResponse<RulebookResponse | undefined>> => {
  const tractsList = tracts.map(
    t =>
      ({
        startDate: moment(t.fechaInicio).format('YYYY-MM-DD'),
        endDate: moment(t.fechaFin).format('YYYY-MM-DD'),
        dailyQuantity: t.cantidadDiaria
      }) as CheckPresentFutureDatesTractRequest
  )
  const request: CheckPresentFutureDatesRulebookRequest = {
    tradeId: tradeId,
    positionId: positionId,
    counterpart: counterpart,
    tracts: tractsList
  }
  return await apiTrades
    .CheckPresentFutureDatesRulebook(request)
    .then(response => {
      return createSuccessServiceResponse(response)
    })
    .catch(error => {
      return createErrorServiceResponse(error)
    })
}

export const checkDeletePositionRulebook = async (
  positionId: number,
  counterpart?: number
): Promise<ServiceResponse<RulebookResponse | undefined>> => {
  const request: CheckDeletePositionRulebookRequest = {
    positionId: positionId,
    counterpart: counterpart
  }
  return await apiTrades
    .CheckDeletePositionRulebook(request)
    .then(response => {
      return createSuccessServiceResponse(response)
    })
    .catch(error => {
      return createErrorServiceResponse(error)
    })
}

export const checkDeleteTractRulebook = async (
  tradeId: number,
  positionId: number,
  tractStartDate: Date,
  tractEndDate: Date,
  company?: number,
  counterpart?: number
): Promise<ServiceResponse<RulebookResponse | undefined>> => {
  const request: CheckDeleteTractRulebookRequest = {
    tradeId: tradeId,
    positionId: positionId,
    tractStartDate: moment(tractStartDate).format('YYYY-MM-DD'),
    tractEndDate: moment(tractEndDate).format('YYYY-MM-DD'),
    company: company ?? 1,
    counterpart: counterpart
  }
  return await apiTrades
    .CheckDeleteTractRulebook(request)
    .then(response => {
      return createSuccessServiceResponse(response)
    })
    .catch(error => {
      return createErrorServiceResponse(error)
    })
}

export const validateTradeRulebook = async (
  trade: ValidateTradeRulebookModel,
  positions?: NewPosicionPrecio[],
  oldCounterpartId?: number
): Promise<ServiceResponse<RulebookResponse | undefined>> => {
  const request: ValidateTradeRulebookRequest = {
    trade: {
      ...trade,
      initDate: moment(trade.initDate).format('YYYY-MM-DD'),
      endDate: moment(trade.endDate).format('YYYY-MM-DD'),
      signDate: moment(trade.signDate).format('YYYY-MM-DD'),
      positions:
        positions && positions.length > 0
          ? positions.map(pos => ({
              initDate: moment(pos.fechaInicio).format('YYYY-MM-DD'),
              endDate: moment(pos.fechaFin).format('YYYY-MM-DD')
            }))
          : [
              {
                initDate: moment(trade.initDate).format('YYYY-MM-DD'),
                endDate: moment(trade.endDate).format('YYYY-MM-DD')
              }
            ]
    },
    oldCounterpartId
  }
  return await apiTrades
    .ValidateTradeRulebook(request)
    .then(response => {
      return createSuccessServiceResponse(response)
    })
    .catch(error => {
      return createErrorServiceResponse(error)
    })
}

export const validatePositionRulebook = async (
  position: PosicionPrecio,
  tradeId: number | null,
  counterpartId: number,
  tradeDates?: DatesInterval,
  deletePosition?: boolean,
  complexView: boolean = true
): Promise<ServiceResponse<RulebookResponse | undefined>> => {
  const startDates = position.tramos.map(t => moment(t.fechaInicio))
  const endDates = position.tramos.map(t => moment(t.fechaFin))
  const minStartDate = moment.min(startDates)
  const maxEndDate = moment.max(endDates)

  const tracts: CheckPresentFutureDatesTractRequest[] =
    !complexView && position.tramos.length === 1
      ? [
          {
            startDate: moment(position.fechaInicio).format('YYYY-MM-DD'),
            endDate: moment(position.fechaFin).format('YYYY-MM-DD'),
            dailyQuantity: position.tramos[0].cantidadDiaria ?? 0
          }
        ]
      : position.tramos.map(tract => ({
          startDate: moment(tract.fechaInicio).format('YYYY-MM-DD'),
          endDate: moment(tract.fechaFin).format('YYYY-MM-DD'),
          dailyQuantity: tract.cantidadDiaria ?? 0
        }))
  const positionRequest: ValidatePositionRulebookModel = {
    tradeId: tradeId ?? 0,
    positionId: position.idPosicion ?? 0,
    startDatePosition: minStartDate.format('YYYY-MM-DD'),
    endDatePosition: maxEndDate.format('YYYY-MM-DD'),
    positionType: enumEnumToValueNumber(EnumTipoPosicion, position.tipoPosicion)!,
    deliveryPointId: position?.puntoEntrega?.id || position?.puntoEntregaId || null,
    tracts: tracts
  }
  const request: ValidatePositionRulebookRequest = {
    position: positionRequest,
    tradeStartDate: moment(position.fechaInicio).format('YYYY-MM-DD'),
    tradeEndDate: moment(position.fechaFin).format('YYYY-MM-DD'),
    counterpartId,
    deletePosition
  }
  return await apiTrades
    .ValidatePositionRulebook(request)
    .then(response => {
      return createSuccessServiceResponse(response)
    })
    .catch(error => {
      return createErrorServiceResponse(error)
    })
}

export const validateTractRulebook = async (
  tradeId: number | null,
  positionId: number | null,
  tract: NewTramo,
  positionStartDate?: Date,
  positionEndDate?: Date
): Promise<ServiceResponse<RulebookResponse | undefined>> => {
  const tractRequest: ValidateTractRulebookModel = {
    startDate: moment(tract.fechaInicio).format('YYYY-MM-DD'),
    endDate: moment(tract.fechaFin).format('YYYY-MM-DD'),
    quantity: tract.cantidadDiaria!
  }
  const request: ValidateTractRulebookRequest = {
    tradeId: tradeId ?? 0,
    positionId: positionId ?? 0,
    tractId: tract.idTramo ?? 0,
    positionStartDate: positionStartDate
      ? moment(positionStartDate).format('YYYY-MM-DD')
      : undefined,
    positionEndDate: positionEndDate ? moment(positionEndDate).format('YYYY-MM-DD') : undefined,
    tract: tractRequest
  }
  return await apiTrades
    .ValidateTractRulebook(request)
    .then(response => {
      return createSuccessServiceResponse(response)
    })
    .catch(error => {
      return createErrorServiceResponse(error)
    })
}

export const createPositionPrice = async (
  tradeID: number,
  newPosition: PosicionPrecio
): Promise<ServiceResponse<any | undefined>> => {
  const request: PatchTradePioneerRequest = mapCreatePositionRequest(newPosition)
  return await apiTrades
    .PatchTrade(tradeID, request)
    .then(response => {
      return createSuccessServiceResponse(response)
    })
    .catch(error => {
      return createErrorServiceResponse(error)
    })
}

export const editPositionPrice = async (
  tradeID: number,
  position: PosicionPrecio,
  modifiedProps: Partial<PosicionPrecioDiff>
): Promise<ServiceResponse<any | undefined>> => {
  const request: PatchTradePioneerRequest = mapEditPositionRequest(
    position.idPosicion,
    modifiedProps
  )
  return await apiTrades
    .PatchTrade(tradeID, request)
    .then(response => {
      return createSuccessServiceResponse(response)
    })
    .catch(error => {
      return createErrorServiceResponse(error)
    })
}

export const deletePosition = async (
  tradeID: number,
  positionID: number
): Promise<ServiceResponse<any | undefined>> => {
  const request: PatchTradePioneerRequest = mapDeletePositionRequest(positionID)
  return await apiTrades
    .PatchTrade(tradeID, request)
    .then(response => {
      return createSuccessServiceResponse(response)
    })
    .catch(error => {
      return createErrorServiceResponse(error)
    })
}
